import React, { FC } from 'react';
import { ArrowDownIcon } from '@src/svg';
import { Column } from '@uikit/Tables/types';
import Pagination from '@uikit/Pagination/Pagination';
import './ColumnsTableSkeleton.scss';

const noop = () => undefined;

export type ColumnsSkeletonOptions = {
  iconAvatar?: boolean;
};

const TEXT_WIDTHS = ['72%', '56%', '84%', '64%', '76%'];
const NAME_WIDTHS = ['82%', '64%', '90%', '70%', '78%'];

const SkeletonText: FC<{ width: string }> = ({ width }) => (
  <span
    className="truncatedText"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      minWidth: 0,
      lineHeight: 1.8,
    }}
  >
    <span
      className="columns-table-skeleton__text"
      style={{
        display: 'inline-block',
        flex: '0 1 auto',
        width,
        minWidth: 0,
      }}
    >
      &nbsp;
    </span>
  </span>
);

const SkeletonCell: FC<{
  col: Column;
  rowIndex: number;
  options: ColumnsSkeletonOptions;
}> = ({ col, rowIndex, options }) => {
  if (col.type === 'avatar' && options.iconAvatar) {
    return (
      <div className="table_avatar">
        <div className="table_avatar_icon columns-table-skeleton__block" />
        <div className="table_avatar_info">
          <div className="table_avatar_fullName">
            <SkeletonText width={NAME_WIDTHS[rowIndex % NAME_WIDTHS.length]} />
          </div>
          <div className="table_avatar_relation">
            <h4 />
          </div>
        </div>
      </div>
    );
  }

  if (col.type === 'avatar') {
    return (
      <div className="table_avatar">
        <div className="image_wrapper columns-table-skeleton__block" />
        <div className="table_avatar_info">
          <div className="table_avatar_fullName">
            <SkeletonText width={NAME_WIDTHS[rowIndex % NAME_WIDTHS.length]} />
          </div>
          <div className="table_avatar_relation">
            <h4>
              <span
                className="columns-table-skeleton__text"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'top',
                  width: 90,
                }}
              >
                &nbsp;
              </span>
            </h4>
          </div>
        </div>
      </div>
    );
  }

  if (col.type === 'status') {
    return (
      <div className="table_status">
        <div className="dropdown">
          <button type="button" tabIndex={-1}>
            <div className="status_dropdown columns-table-skeleton__block">
              <span>Inactive</span>
              <ArrowDownIcon width={16} height={16} />
            </div>
          </button>
        </div>
      </div>
    );
  }

  // permission cells: the 24px `table-permission-toggle` with a 16px icon
  if (col.type === 'boolean') {
    return (
      <button type="button" tabIndex={-1} className="table-permission-toggle">
        <span
          className="columns-table-skeleton__block"
          style={{
            display: 'block',
            width: 16,
            height: 16,
            borderRadius: '50%',
          }}
        />
      </button>
    );
  }

  return <SkeletonText width={TEXT_WIDTHS[rowIndex % TEXT_WIDTHS.length]} />;
};

export const ColumnsTableSkeleton: FC<{
  columns: Column[];
  rows?: number;
  options?: ColumnsSkeletonOptions;
}> = ({ columns, rows = 5, options = {} }) => (
  <div className="custom-table-wrapper columns-table-skeleton" aria-busy="true">
    <div className="t-scroll">
      <table className="custom-table">
        <thead className="thead-title">
          <tr className="t-row">
            {columns.map((col) => (
              <th
                key={col.field}
                className={`sortable-header ${col.className ?? ''}`}
              >
                <span className="table-header-content">
                  <span className="table-header-label columns-table-skeleton__text">
                    {col.headerName}
                  </span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex} className="draggable-row">
              {columns.map((col) => (
                <td
                  key={col.field}
                  className={
                    col.type === 'status' || col.type === 'buttons'
                      ? 'non_hidden'
                      : ''
                  }
                >
                  <SkeletonCell
                    col={col}
                    rowIndex={rowIndex}
                    options={options}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {/* the real Pagination (same markup/classes as CustomTable renders),
        inert so the placeholder can't be focused or used */}
    <div style={{ display: 'contents' }} {...{ inert: '' }}>
      <Pagination
        size={rows}
        totalElements={rows * 2 + 1}
        itemsCount={rows}
        currentPage={1}
        handlePageChange={noop}
        handleSizeChange={noop}
        dataTestId="columns-table-skeleton"
      />
    </div>
  </div>
);
